import React, { useState } from 'react';
import {
  Box,
  alpha,
  useTheme,
  Typography,
  Dialog,
  IconButton,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';

interface ImageOption {
  label: string;
  imageUrl: string;
}

interface ImageChoiceFieldProps {
  value: string | null;
  onChange: (value: string | null) => void;
  options: ImageOption[];
  disabled?: boolean;
}

const ImageChoiceField: React.FC<ImageChoiceFieldProps> = ({ value, onChange, options, disabled }) => {
  const theme = useTheme();
  const [lightboxOption, setLightboxOption] = useState<ImageOption | null>(null);

  if (!options || options.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
        No options configured
      </Typography>
    );
  }

  const handleImageClick = (option: ImageOption, e: React.MouseEvent) => {
    e.stopPropagation();
    setLightboxOption(option);
  };

  const handleSelect = (option: ImageOption) => {
    if (disabled) return;
    const isSelected = value === option.label;
    onChange(isSelected ? null : option.label);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          gap: 2,
          mt: 2,
        }}
      >
        {options.map((option) => {
          const isSelected = value === option.label;
          return (
            <Box
              key={option.label}
              sx={{
                position: 'relative',
                borderRadius: 2,
                overflow: 'hidden',
                width: { xs: '100%', sm: 200 },
                maxWidth: 200,
                border: `2px solid ${isSelected ? theme.palette.primary.main : alpha(theme.palette.divider, 0.3)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  '& .zoom-overlay': {
                    opacity: 1,
                  },
                },
              }}
            >
              {/* Image with zoom overlay */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: 140,
                  cursor: 'pointer',
                }}
                onClick={(e) => handleImageClick(option, e)}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: '100%',
                    backgroundImage: `url(${option.imageUrl})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                  }}
                />
                {/* Zoom overlay */}
                <Box
                  className="zoom-overlay"
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: alpha('#000', 0.5),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                  }}
                >
                  <ZoomInIcon sx={{ fontSize: 32, color: 'white' }} />
                </Box>
              </Box>

              {/* Label - clickable to select */}
              <Box
                onClick={() => handleSelect(option)}
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'background.paper',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s',
                  '&:hover': {
                    bgcolor: isSelected
                      ? alpha(theme.palette.primary.main, 0.12)
                      : alpha(theme.palette.action.hover, 0.08),
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 600 : 400,
                    color: isSelected ? 'primary.main' : 'text.primary',
                    textAlign: 'left',
                    flex: 1,
                    lineHeight: 1.3,
                  }}
                >
                  {option.label}
                </Typography>
                {isSelected ? (
                  <CheckCircleIcon sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }}
                  />
                )}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Lightbox Dialog */}
      <Dialog
        open={!!lightboxOption}
        onClose={() => setLightboxOption(null)}
        maxWidth="lg"
        PaperProps={{
          sx: {
            bgcolor: 'transparent',
            boxShadow: 'none',
            overflow: 'visible',
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.9)',
            },
          },
        }}
      >
        {lightboxOption && (
          <Box sx={{ position: 'relative' }}>
            {/* Close button */}
            <IconButton
              onClick={() => setLightboxOption(null)}
              sx={{
                position: 'absolute',
                top: -40,
                right: 0,
                color: 'white',
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Large image */}
            <Box
              component="img"
              src={lightboxOption.imageUrl}
              alt={lightboxOption.label}
              sx={{
                maxWidth: '90vw',
                maxHeight: '80vh',
                borderRadius: 2,
                display: 'block',
              }}
            />
          </Box>
        )}
      </Dialog>
    </>
  );
};

export default ImageChoiceField;
